import { ChangeDetectionStrategy, Component, computed, effect, input } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { InputComponent, Tooth, ToothSide } from '@libs/ui-lib';
import { TranslatePipe } from '@ngx-translate/core';
import { isReversedMeasurementOrder } from '../../../perio-exam-screen/services/perio-exam/utils';

interface NextToothPreviewForm {
  surface: FormControl<number | undefined>;
}

@Component({
  selector: 'lib-perio-next-pocket-depth',
  imports: [InputComponent, TranslatePipe, ReactiveFormsModule],
  templateUrl: './perio-next-pocket-depth.component.html',
  styleUrl: './perio-next-pocket-depth.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class PerioNextPocketDepthComponent {
  public toothPosition = input.required<number>();
  public toothSide = input.required<ToothSide>();
  public tooth = input<Tooth | null>(null);

  /**
   * Calculate the first surface to be measured based on tooth position and side.
   * When measurement order is reversed (M→C→D), the first surface is 'mesial'.
   * Otherwise, it's 'distal' (D→C→M).
   */
  protected nextSurface = computed(() => {
    const isReversed = isReversedMeasurementOrder(this.toothPosition(), this.toothSide());
    return isReversed ? 'mesial' : 'distal';
  });

  /**
   * Translation key for the surface letter (M or D)
   */
  protected surfaceTranslationKey = computed(() => {
    return this.nextSurface() === 'mesial' ? 'perio_exam_measurement_mesial_letter' : 'perio_exam_measurement_distal_letter';
  });

  /**
   * Get the existing pocket depth value for the next surface
   */
  protected existingValue = computed(() => {
    const tooth = this.tooth();
    if (!tooth) return;

    const side = this.toothSide();
    const surface = this.nextSurface();
    const pocketDepth = tooth.perio[side]?.pocketDepth;

    if (!pocketDepth) return;

    const value = pocketDepth[surface];
    return value === 0 ? undefined : value;
  });

  protected form: FormGroup<NextToothPreviewForm> = new FormGroup({
    surface: new FormControl<number | undefined>(undefined, { nonNullable: true }),
  });

  constructor() {
    // Update form value when existingValue changes
    effect(() => {
      const value = this.existingValue();
      this.form.controls.surface.setValue(value, { emitEvent: false });
    });
  }
}
